import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { List, ListItem, Table } from "@mantine/core";

import { Fragment } from "react/jsx-runtime";

import { TextWrapper } from "@/components/TextWrapper";
import PostpartumTimeCell from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/PostpartumTable/PostpartumTimeCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getIPPVitalsRowValues } from "@/encounters/components/EncounterLabor/utils/postpartum";

interface IppVitalsRowProps {
  time: string;
  observation: Observation | undefined;
  showNumbersOnly?: boolean;
  showPreviousVersions?: boolean;
  onRefresh?: () => Promise<void>;
  isViewOnly: boolean;
}

export default function IppVitalsRow({
  time,
  observation,
  showNumbersOnly,
  showPreviousVersions = true,
  onRefresh,
  isViewOnly,
}: IppVitalsRowProps) {
  const {
    bloodPressure,
    pulse,
    temperature,
    fundus,
    fundusValue,
    fundalTexture,
    estimatedBloodLoss,
    quantitativeBloodLoss,
    notes,
    hasAnswers,
    hasNotes,
  } = useMemo(() => getIPPVitalsRowValues(observation), [observation]);
  const fundusRow = fundus
    ? `${fundus}${fundusValue ? `: ${fundusValue}` : ""}${fundalTexture ? ` (${fundalTexture})` : ""}`
    : fundalTexture;

  if (!observation) return null;

  const previousVersions = hasPreviousVersions(observation);

  const detailsRowNeeded = !showNumbersOnly && (hasAnswers || hasNotes);
  const mainRowRenders = hasAnswers && showPreviousVersions;

  return (
    <Fragment>
      {mainRowRenders && (
        <Table.Tr bg="white">
          <PostpartumTimeCell
            time={time}
            onRefresh={onRefresh}
            isViewOnly={isViewOnly}
            observation={observation}
            rowSpan={detailsRowNeeded ? 2 : 1}
          />
          <Table.Td>{bloodPressure}</Table.Td>
          <Table.Td>{temperature}</Table.Td>
          <Table.Td>{pulse}</Table.Td>
          <Table.Td>{fundusRow}</Table.Td>
          <Table.Td>{estimatedBloodLoss}</Table.Td>
        </Table.Tr>
      )}
      {detailsRowNeeded && (
        <Table.Tr bg="white" className={classes.tableRow}>
          {!mainRowRenders && showPreviousVersions && (
            <PostpartumTimeCell
              time={time}
              rowSpan={1}
              onRefresh={onRefresh}
              isViewOnly={isViewOnly}
              observation={observation}
            />
          )}
          <Table.Td p="md" colSpan={mainRowRenders ? 5 : 6}>
            <>
              <TextWrapper pt="xs" size="sm">
                IPP Vitals:
              </TextWrapper>
              <List fz="sm" withPadding>
                {bloodPressure && (
                  <ListItem>
                    <strong>Blood Pressure:</strong> {bloodPressure}
                  </ListItem>
                )}
                {temperature && (
                  <ListItem>
                    <strong>Temperature:</strong> {temperature}
                  </ListItem>
                )}
                {pulse && (
                  <ListItem>
                    <strong>Heart Rate:</strong> {pulse}
                  </ListItem>
                )}
                {fundusRow && (
                  <ListItem>
                    <strong>Fundus:</strong> {fundusRow}
                  </ListItem>
                )}
                {estimatedBloodLoss && (
                  <ListItem>
                    <strong>Estimated Blood Loss:</strong> {estimatedBloodLoss}
                  </ListItem>
                )}
                {quantitativeBloodLoss && (
                  <ListItem>
                    <strong>Quantitative Blood Loss:</strong> {quantitativeBloodLoss}
                  </ListItem>
                )}
              </List>
            </>
            {hasNotes && !showNumbersOnly && (
              <TextWrapper pt="xs" size="sm">
                IPP Vitals Comments: {notes}
              </TextWrapper>
            )}
            {showPreviousVersions && previousVersions ? (
              <PreviousVersions
                observation={observation}
                rowComponent={IppVitalsRow}
                showPreviousVersions={showPreviousVersions}
              />
            ) : (
              <LastUpdated observation={observation} showPreviousVersions={false} />
            )}
          </Table.Td>
        </Table.Tr>
      )}
    </Fragment>
  );
}
